import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaCheck, FaTimes, FaClock, FaBook, FaGraduationCap, 
  FaCalculator, FaRulerCombined, FaRedoAlt, FaChartBar, 
  FaTrophy, FaStar, FaShare, FaArrowRight, FaPrint, 
  FaChevronDown, FaChevronUp, FaMedal, FaRegLightbulb, FaRegSmile,
  FaHeart, FaLightbulb, FaSave, FaUser
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

import { 
  GiBrain, GiLaurelsTrophy, GiTrophyCup, GiMedal, 
  GiRocket, GiCrown, GiTargetArrows 
} from 'react-icons/gi';
import { 
  MdEmojiEvents, MdWorkspacePremium, MdTrendingUp, 
  MdAutoAwesome, MdInsights, MdBarChart 
} from 'react-icons/md';
import { 
  IoRocketSharp, IoSparkles, IoTrophy 
} from 'react-icons/io5';
import { 
  BsAward, BsStars, BsGraphUp, BsLightbulb 
} from 'react-icons/bs';
import RatingForm from '../common/RatingForm';

import Header from './Header';
import Footer from './Footer';

 const extractTestData = (test) => {
  if (!test) return null;
  
  return {
    id: test.id,
    title: test.title,
    description: test.description,
    levels: test.levels?.map(level => ({
      levelTitle: level.levelTitle,
      name: level.name,
      questions: level.questions?.map(q => ({
        questionText: q.questionText,
        options: q.options,
        correctAnswer: q.correctAnswer,
        explanation: q.explanation
      }))
    })),
    hearts: test.hearts,
    hints: test.hints,
    allQuestions: test.allQuestions?.map(q => ({
      questionText: q.questionText,
      options: q.options,
      correctAnswer: q.correctAnswer,
      explanation: q.explanation
    }))
  };
};

 const extractAnswersData = (answers) => {
  if (!answers) return [];
  
  return answers.map(answer => ({
    selectedIndex: answer.selectedIndex,
    isCorrect: answer.isCorrect,
    timeSpent: answer.timeSpent,
    timeUp: answer.timeUp
  }));
};

const TestResultsScreen = ({ 
  currentTest: initialCurrentTest, 
  userAnswers: initialUserAnswers, 
  onRestartTest,
  onBackToList, 
  scrollToSection 
}) => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('summary');
  const [expandedLevels, setExpandedLevels] = useState({});
  const [showCertificate, setShowCertificate] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  
   const [currentTest, setCurrentTest] = useState(() => {
     if (location.state?.currentTest) {
      return location.state.currentTest;
    } else if (initialCurrentTest) {
      return initialCurrentTest;
    } else {
      const saved = localStorage.getItem('testResults_currentTest');
      return saved ? JSON.parse(saved) : null;
    }
  });
  
  const [userAnswers, setUserAnswers] = useState(() => {
    if (location.state?.userAnswers) {
      return location.state.userAnswers;
    } else if (initialUserAnswers) {
      return initialUserAnswers;
    } else {
      const saved = localStorage.getItem('testResults_userAnswers');
      return saved ? JSON.parse(saved) : [];
    }
  });

   useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
   useEffect(() => {
    if (currentTest && userAnswers.length > 0) {
      const testData = extractTestData(currentTest);
      const answersData = extractAnswersData(userAnswers);
      
      localStorage.setItem('testResults_currentTest', JSON.stringify(testData));
      localStorage.setItem('testResults_userAnswers', JSON.stringify(answersData));
      localStorage.setItem('testResults_timestamp', Date.now().toString());
      
      setHasResults(true);
      console.log('تم حفظ النتائج في localStorage');
    } else {
      setHasResults(false);
    }
  }, [currentTest, userAnswers]);

   useEffect(() => {
    const cleanupOldData = () => {
      const timestamp = localStorage.getItem('testResults_timestamp');
      if (timestamp) {
        const age = Date.now() - parseInt(timestamp);
         if (age > 60 * 60 * 1000) {
          localStorage.removeItem('testResults_currentTest');
          localStorage.removeItem('testResults_userAnswers');
          localStorage.removeItem('testResults_timestamp');
          console.log('تم تنظيف البيانات القديمة');
        }
      }
    };

     cleanupOldData();

     const handleBeforeUnload = () => {
      cleanupOldData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
     };
  }, []);

   const backgroundIcons = useMemo(() => {
    const iconComponents = [
      FaBook, FaGraduationCap, FaCalculator, FaRulerCombined,
      GiTrophyCup, GiMedal, BsAward, MdEmojiEvents
    ];

    const numberOfIcons = isMobile ? 8 : 12;
    
    return Array.from({ length: numberOfIcons }, (_, index) => {
      const RandomIcon = iconComponents[Math.floor(Math.random() * iconComponents.length)];
      
      return {
        id: index,
        Icon: RandomIcon,
        size: isMobile ? Math.random() * 8 + 8 : Math.random() * 15 + 15,
        left: Math.random() * 100,
        top: Math.random() * 100,
        rotation: Math.random() * 360,
        opacity: Math.random() * 0.04 + 0.01,
        animationDelay: Math.random() * 5
      };
    });
  }, [isMobile]);

   const toggleLevel = (levelIndex) => {
    setExpandedLevels(prev => ({
      ...prev,
      [levelIndex]: !prev[levelIndex]
    }));
  };

   const handleLoginRedirect = () => {
    setIsRedirecting(true);
    
     const testData = extractTestData(currentTest);
    const answersData = extractAnswersData(userAnswers);
    
    localStorage.setItem('testResults_currentTest', JSON.stringify(testData));
    localStorage.setItem('testResults_userAnswers', JSON.stringify(answersData));
    localStorage.setItem('testResults_timestamp', Date.now().toString());
    localStorage.setItem('testResults_redirectPath', location.pathname);
    
    console.log('تم حفظ البيانات قبل الانتقال إلى تسجيل الدخول');
    
     setTimeout(() => {
      navigate('/login');
    }, 500);
  };

   const calculateResults = () => {
    if (!currentTest || !userAnswers || userAnswers.length === 0) {
      return {
        totalQuestions: 0,
        correctAnswers: 0,
        wrongAnswers: 0,
        unansweredQuestions: 0,
        score: 0,
        levelResults: [],
        timeAnalysis: {
          averageTime: 0,
          fastestAnswer: 0,
          slowestAnswer: 0,
          totalTime: 0,
          timeUpAnswers: 0
        },
        strengthsAndWeaknesses: { strengths: [], weaknesses: [] },
        additionalStats: {
          heartsUsed: 0,
          hintsUsed: 0,
          accuracy: 0,
          completionRate: 0
        }
      };
    }

     const totalQuestions = currentTest.levels?.reduce((total, level) => 
      total + (level.questions?.length || 0), 0) || 
      (currentTest.allQuestions?.length || 0);

     const correctAnswers = userAnswers.filter(answer => 
      answer && answer.isCorrect === true
    ).length;

     const wrongAnswers = userAnswers.filter(answer => 
      answer && answer.isCorrect === false
    ).length;

     const unansweredQuestions = totalQuestions - userAnswers.length;

    const score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    
     const levelResults = currentTest.levels?.map((level, levelIndex) => {
      const levelQuestions = level.questions?.length || 0;
      const startIndex = currentTest.levels.slice(0, levelIndex).reduce((total, l) => 
        total + (l.questions?.length || 0), 0);
      const levelAnswers = userAnswers.slice(startIndex, startIndex + levelQuestions);
      const levelCorrect = levelAnswers.filter(answer => 
        answer && answer.isCorrect === true
      ).length;
      
      return {
        name: level.levelTitle || level.name || `المستوى ${levelIndex + 1}`,
        totalQuestions: levelQuestions,
        correctAnswers: levelCorrect,
        wrongAnswers: levelAnswers.filter(answer => 
          answer && answer.isCorrect === false
        ).length,
        score: levelQuestions > 0 ? Math.round((levelCorrect / levelQuestions) * 100) : 0
      };
    }) || [];
    
     const timeAnalysis = {
      averageTime: userAnswers.length > 0
        ? Math.round(userAnswers.reduce((sum, answer) => sum + (answer?.timeSpent || 0), 0) / userAnswers.length)
        : 0,
      fastestAnswer: userAnswers.length > 0 ? Math.min(...userAnswers.map(answer => answer?.timeSpent || Infinity)) : 0,
      slowestAnswer: userAnswers.length > 0 ? Math.max(...userAnswers.map(answer => answer?.timeSpent || 0)) : 0,
      totalTime: userAnswers.reduce((sum, answer) => sum + (answer?.timeSpent || 0), 0),
      timeUpAnswers: userAnswers.filter(answer => answer?.timeUp).length
    };
    
     const strengthsAndWeaknesses = analyzeStrengthsAndWeaknesses(userAnswers, currentTest.levels);
    
     const additionalStats = {
      heartsUsed: (currentTest?.hearts?.total || 3) - (currentTest?.hearts?.remaining || 0),
      hintsUsed: (currentTest?.hints?.total || 2) - (currentTest?.hints?.remaining || 0),
      accuracy: totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0,
      completionRate: Math.round((userAnswers.length / totalQuestions) * 100)
    };
    
    return {
      totalQuestions,
      correctAnswers,
      wrongAnswers,
      unansweredQuestions,
      score,
      levelResults,
      timeAnalysis,
      strengthsAndWeaknesses,
      additionalStats
    };
  };
  
   const analyzeStrengthsAndWeaknesses = (answers, levels) => {
    if (!levels || levels.length === 0) {
      return { strengths: [], weaknesses: [] };
    }

    const levelPerformance = levels.map((level, levelIndex) => {
      const startIndex = levels.slice(0, levelIndex).reduce((total, l) => 
        total + (l.questions?.length || 0), 0);
      const endIndex = startIndex + (level.questions?.length || 0);
      const levelAnswers = answers.slice(startIndex, endIndex);
      const correctRatio = levelAnswers.length > 0 ? 
        levelAnswers.filter(a => a && a.isCorrect === true).length / levelAnswers.length : 0;
      
      return {
        levelName: level.levelTitle || level.name || `المستوى ${levelIndex + 1}`,
        correctRatio,
        index: levelIndex
      };
    });
    
    const sortedByPerformance = [...levelPerformance].sort((a, b) => b.correctRatio - a.correctRatio);
    
    return {
      strengths: sortedByPerformance.slice(0, Math.ceil(sortedByPerformance.length / 2))
        .map(l => ({
          name: l.levelName,
          ratio: Math.round(l.correctRatio * 100)
        })),
      weaknesses: sortedByPerformance.slice(Math.ceil(sortedByPerformance.length / 2))
        .map(l => ({
          name: l.levelName,
          ratio: Math.round(l.correctRatio * 100)
        }))
        .filter(w => w.ratio < 70)
    };
  };

   const getMotivationalMessage = () => {
    const { score, correctAnswers, totalQuestions } = results;
    
    if (score >= 95) {
      return {
        title: "ممتاز! 🎉",
        message: `أداء استثنائي! ${correctAnswers}/${totalQuestions}`,
        color: "from-green-500 to-emerald-600",
        bgColor: "bg-gradient-to-r from-green-50 to-emerald-50",
        icon: "🥇"
      };
    } else if (score >= 85) {
      return {
        title: "رائع! 👏",
        message: `نتيجة ممتازة! ${correctAnswers}/${totalQuestions}`,
        color: "from-blue-500 to-cyan-600",
        bgColor: "bg-gradient-to-r from-blue-50 to-cyan-50",
        icon: "🥈"
      };
    } else if (score >= 70) {
      return {
        title: "جيد جداً! 👍",
        message: `أداء قوي! ${correctAnswers}/${totalQuestions}`,
        color: "from-indigo-500 to-purple-600",
        bgColor: "bg-gradient-to-r from-indigo-50 to-purple-50",
        icon: "🥉"
      };
    } else if (score >= 50) {
      return {
        title: "مقبول 💪",
        message: `استمر في التحسن! ${correctAnswers}/${totalQuestions}`,
        color: "from-amber-500 to-orange-500",
        bgColor: "bg-gradient-to-r from-amber-50 to-orange-50",
        icon: "📚"
      };
    } else {
      return {
        title: "حاول مرة أخرى 💫",
        message: `لا تستسلم! ${correctAnswers}/${totalQuestions}`,
        color: "from-rose-500 to-pink-600",
        bgColor: "bg-gradient-to-r from-rose-50 to-pink-50",
        icon: "🔄"
      };
    }
  };

   const getImprovementSuggestions = () => {
    const { score, additionalStats, timeAnalysis } = results;
    
    const suggestions = [];
    
    if (score < 70) {
      suggestions.push("راجع الأسئلة الخاطئة وحاول فهم الأخطاء");
    }
    
    if (timeAnalysis.timeUpAnswers > 0) {
      suggestions.push("تدرب على تحسين سرعة الإجابة");
    }
    
    if (additionalStats.hintsUsed > 0) {
      suggestions.push("حاول الاعتماد على معرفتك بدلاً من التلميحات");
    }
    
    if (score >= 85) {
      suggestions.push("جرب اختبارات أكثر صعوبة لتطوير مهاراتك");
      suggestions.push("شارك معرفتك مع الآخرين");
    } else {
      suggestions.push("كرر الاختبار لتحسين نتيجتك");
      suggestions.push("راجع الدروس ذات الصلة بالأسئلة الخاطئة");
    }
    
    return suggestions.slice(0, 3);
  };

   const saveResults = async (isAutoSave = false) => {
    if (!user) {
       handleLoginRedirect();
      return false;
    }

    if (isAutoSave) {
      setIsAutoSaving(true);
    } else {
      setIsSaving(true);
    }
    
    setSaveStatus(null);

    try {
       const resultsData = {
        testId: currentTest.id || "math-test-001",
        testTitle: currentTest.title || "اختبار الرياضيات المتقدم",
        score: results.score,
        correctAnswers: results.correctAnswers,
        totalQuestions: results.totalQuestions,
        wrongAnswers: results.wrongAnswers,
        unansweredQuestions: results.unansweredQuestions,
        duration: `${Math.floor(results.timeAnalysis.totalTime / 60)}:${String(results.timeAnalysis.totalTime % 60).padStart(2, '0')}`,
        totalTimeSeconds: results.timeAnalysis.totalTime,
        levelResults: results.levelResults,
        timeAnalysis: results.timeAnalysis,
        strengthsAndWeaknesses: results.strengthsAndWeaknesses,
        additionalStats: results.additionalStats,
        userAnswers: userAnswers.map((answer, index) => {
          const allQuestions = currentTest.levels?.flatMap(level => level.questions || []) || [];
          const question = allQuestions[index] || {};
          
          return {
            questionIndex: index,
            selectedIndex: answer.selectedIndex,
            isCorrect: answer.isCorrect,
            timeSpent: answer.timeSpent,
            timeUp: answer.timeUp,
            questionText: question.questionText || `سؤال ${index + 1}`,
            correctAnswer: question.correctAnswer || "",
            userAnswer: question.options?.[answer.selectedIndex] || ""
          };
        }),
        performanceLevel: getPerformanceLevel(results.score),
        userId: user.id || user._id,
        userName: getUserName(),
        date: formatDate()
      };

       const response = await fetch('https://question-manager-backend.onrender.com/api/result', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(resultsData)
      });

      if (response.ok) {
        if (!isAutoSave) {
          setSaveStatus({
            type: 'success',
            message: 'تم حفظ النتائج بنجاح'
          });
        }
         localStorage.removeItem('testResults_currentTest');
        localStorage.removeItem('testResults_userAnswers');
        localStorage.removeItem('testResults_timestamp');
        localStorage.removeItem('testResults_redirectPath');
        
        return true;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'فشل في حفظ النتائج');
      }
    } catch (error) {
      console.error('Error saving results:', error);
      if (!isAutoSave) {
        setSaveStatus({
          type: 'error',
          message: error.message || 'حدث خطأ أثناء حفظ النتائج'
        });
      }
      return false;
    } finally {
      if (isAutoSave) {
        setIsAutoSaving(false);
      } else {
        setIsSaving(false);
      }
    }
  };

   const getPerformanceLevel = (score) => {
    if (score >= 95) return "ممتاز";
    if (score >= 85) return "متميز";
    if (score >= 75) return "جيد جداً";
    if (score >= 65) return "جيد";
    return "مقبول";
  };

   const formatDate = () => {
    const date = new Date();
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

   const getUserName = () => {
    if (!user) return "مستخدم";
    if (typeof user === 'string') return user;
    if (user.fullname) return user.fullname;
    if (user.name) return user.name;
    if (user.email) return user.email.split('@')[0];
    return "مستخدم";
  };

   const handleCertificateClick = async () => {
    if (user) {
       const saveSuccess = await saveResults(true);
      
      if (saveSuccess) {
        const testResultsData = {
          testTitle: currentTest.title,
          score: results.score,
          correctAnswers: results.correctAnswers,
          totalQuestions: results.totalQuestions,
          duration: `${Math.floor(results.timeAnalysis.totalTime / 60)}:${String(results.timeAnalysis.totalTime % 60).padStart(2, '0')}`,
          level: getPerformanceLevel(results.score),
          date: formatDate(),
          savedToServer: true
        };
        
        navigate(`/certificate/${currentTest.id}`, { 
          state: { testResults: testResultsData } 
        });
      } else {
        setSaveStatus({
          type: 'error',
          message: 'فشل في حفظ النتائج'
        });
      }
    } else {
       handleLoginRedirect();
    }
  };

   const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

   const handleRestartTest = () => {
     localStorage.removeItem('testResults_currentTest');
    localStorage.removeItem('testResults_userAnswers');
    localStorage.removeItem('testResults_timestamp');
    localStorage.removeItem('testResults_redirectPath');
    
    onRestartTest();
  };

  const results = calculateResults();
  const motivationalData = getMotivationalMessage();
  const improvementSuggestions = getImprovementSuggestions();

   if (!currentTest || userAnswers.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" dir="rtl">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">لا توجد نتائج لعرضها</h2>
          <p className="text-gray-600 mb-6">لم يتم العثور على بيانات الاختبار. يرجى إعادة الاختبار.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#320A6B] text-white font-medium rounded-lg hover:bg-[#2a095a] transition-all duration-300"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden" dir="rtl">
      <Header scrollToSection={scrollToSection} />
      
      <div className="relative py-4 bg-white overflow-hidden">
         {backgroundIcons.map((icon) => (
          <icon.Icon
            key={icon.id}
            className="absolute text-gray-200 floating"
            style={{
              left: `${icon.left}%`,
              top: `${icon.top}%`,
              fontSize: `${icon.size}px`,
              transform: `rotate(${icon.rotation}deg)`,
              opacity: icon.opacity,
              animationDelay: `${icon.animationDelay}s`
            }}
          />
        ))}

        <div className="relative px-3 mx-auto max-w-2xl">
           <div className="bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
            <div className="p-4">
               <div className="text-center mb-4">
                <div className="inline-block px-3 py-1 mb-2 bg-gray-100 rounded-full">
                  <h3 className="text-xs font-medium text-gray-700">تم الانتهاء</h3>
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">{currentTest.title}</h2>
                <p className="text-xs text-gray-600">تاريخ {formatDate()}</p>
              </div>
              
               <div className="mb-4 p-2 rounded-lg text-center text-sm bg-gray-50 border border-gray-200">
                {user ? (
                  <div className="flex items-center justify-center text-green-600">
                    <FaUser className="ml-1" />
                    <span>مرحباً {getUserName()}!</span>
                  </div>
                ) : (
                  <div className="text-amber-600">
                    <span>غير مسجل دخول. </span>
                    <button 
                      onClick={handleLoginRedirect}
                      className="text-[#320A6B] font-medium hover:underline"
                      disabled={isRedirecting}
                    >
                      {isRedirecting ? 'جاري الانتقال...' : 'سجل دخول لحفظ النتائج'}
                    </button>
                  </div>
                )}
              </div>
              
               <div className={`${motivationalData.bgColor} rounded-lg p-4 mb-4 border border-gray-200`}>
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-2xl mr-2">{motivationalData.icon}</span>
                    <h3 className="text-base font-bold text-gray-900">{motivationalData.title}</h3>
                  </div>
                  <p className="text-xs text-gray-700 mb-3">{motivationalData.message}</p>
                  
                  <div className="flex items-center justify-center mb-3">
                    <div className="relative w-16 h-16">
                      <svg viewBox="0 0 100 100" className="w-full h-full">
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="#320A6B"
                          strokeWidth="8"
                          strokeDasharray="251.2"
                          strokeDashoffset={251.2 - (251.2 * results.score) / 100}
                          strokeLinecap="round"
                          transform="rotate(-90 50 50)"
                        />
                      </svg>
                      <div className="absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center">
                        <span className="text-lg font-bold text-gray-900">{results.score}%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-600">
                    {results.correctAnswers} صحيحة من {results.totalQuestions}
                  </div>
                </div>
              </div>
              
               {saveStatus && (
                <div className={`mb-4 p-2 rounded-lg text-center text-sm ${
                  saveStatus.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {saveStatus.message}
                </div>
              )}
              
               {isAutoSaving && (
                <div className="mb-4 p-2 rounded-lg text-center text-sm bg-blue-100 text-blue-800 border border-blue-200">
                  جاري حفظ النتائج تلقائياً...
                </div>
              )}
              
               {isRedirecting && (
                <div className="mb-4 p-2 rounded-lg text-center text-sm bg-amber-100 text-amber-800 border border-amber-200">
                  جاري الانتقال إلى صفحة تسجيل الدخول...
                </div>
              )}
              
               <div className="mb-4 overflow-x-auto">
                <div className="flex min-w-max border-b border-gray-200 text-xs">
                  <button 
                    onClick={() => setActiveTab('summary')} 
                    className={`py-2 px-3 flex items-center font-medium ${activeTab === 'summary' ? 'border-b-2 border-[#320A6B] text-[#320A6B]' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <FaChartBar className="ml-1 text-xs" />
                    ملخص
                  </button>
                  <button 
                    onClick={() => setActiveTab('levels')} 
                    className={`py-2 px-3 flex items-center font-medium ${activeTab === 'levels' ? 'border-b-2 border-[#320A6B] text-[#320A6B]' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <FaGraduationCap className="ml-1 text-xs" />
                    المستويات
                  </button>
                  <button 
                    onClick={() => setActiveTab('details')} 
                    className={`py-2 px-3 flex items-center font-medium ${activeTab === 'details' ? 'border-b-2 border-[#320A6B] text-[#320A6B]' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <FaBook className="ml-1 text-xs" />
                    التفاصيل
                  </button>
                </div>
              </div>
              
               {activeTab === 'summary' && (
                <div className="space-y-3">
                   <div className="grid grid-cols-3 gap-2">
                    <div className="bg-green-50 rounded-lg p-2 border border-green-200">
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center ml-1 text-green-600 text-xs">
                          <FaCheck />
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">صحيحة</p>
                          <p className="text-sm font-bold text-gray-900">{results.correctAnswers}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-red-50 rounded-lg p-2 border border-red-200">
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center ml-1 text-red-600 text-xs">
                          <FaTimes />
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">خاطئة</p>
                          <p className="text-sm font-bold text-gray-900">{results.wrongAnswers}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center ml-1 text-gray-600 text-xs">
                          <FaClock />
                        </div>
                        <div>
                          <p className="text-gray-500 text-xs">لم تُجب</p>
                          <p className="text-sm font-bold text-gray-900">{results.unansweredQuestions}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                   <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h4 className="font-bold text-sm mb-2 text-gray-900 flex items-center">
                      <FaClock className="ml-1 text-[#320A6B] text-xs" />
                      تحليل الوقت
                    </h4>
                    
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">المتوسط لكل سؤال</span>
                        <span className="font-medium">{results.timeAnalysis.averageTime} ثانية</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">الوقت الإجمالي</span>
                        <span className="font-medium">{formatTime(results.timeAnalysis.totalTime)}</span>
                      </div>
                      {results.timeAnalysis.timeUpAnswers > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">انتهى وقتها</span>
                          <span className="font-medium text-red-600">{results.timeAnalysis.timeUpAnswers}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                   <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h4 className="font-bold text-sm mb-2 text-gray-900 flex items-center">
                      <BsGraphUp className="ml-1 text-[#320A6B] text-xs" />
                      إحصائيات
                    </h4>
                    
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">معدل الدقة</span>
                        <span className="font-medium">{results.additionalStats.accuracy}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">معدل الإكمال</span>
                        <span className="font-medium">{results.additionalStats.completionRate}%</span>
                      </div>
                      {results.additionalStats.hintsUsed > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">التلميحات المستخدمة</span>
                          <span className="font-medium text-amber-600">{results.additionalStats.hintsUsed}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                   <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <h4 className="font-bold text-sm mb-2 text-gray-900 flex items-center">
                      <BsLightbulb className="ml-1 text-amber-500 text-xs" />
                      اقتراحات للتحسين
                    </h4>
                    
                    <ul className="space-y-1 text-xs">
                      {improvementSuggestions.map((suggestion, index) => (
                        <li key={index} className="flex items-start">
                          <div className="w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center ml-1 mt-0.5 text-amber-600 flex-shrink-0 text-xs">
                            {index + 1}
                          </div>
                          <p className="text-gray-700 leading-tight">{suggestion}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                 <div className="flex flex-wrap gap-2 justify-center pt-2">
    <button
      onClick={onBackToList}
      className="py-2 px-3 bg-gray-200 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-all duration-300 flex items-center justify-center text-xs"
    >
      <FaArrowRight className="ml-1 text-xs" />
      العودة للقائمة
    </button>
    
    <button
      onClick={handleRestartTest}
      className="py-2 px-3 bg-white border border-[#320A6B] text-[#320A6B] font-medium rounded-lg hover:bg-gray-50 transition-all duration-300 flex items-center justify-center text-xs"
    >
      <FaRedoAlt className="ml-1 text-xs" />
      اختبار آخر
    </button>
                    
                    <button
                      onClick={handleCertificateClick}
                      disabled={isAutoSaving || isRedirecting}
                      className={`py-2 px-3 font-medium rounded-lg transition-all duration-300 flex items-center justify-center text-xs ${
                        isAutoSaving || isRedirecting
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                          : 'bg-[#320A6B] text-white hover:bg-[#2a095a]'
                      }`}
                    >
                      <GiTrophyCup className="ml-1 text-xs" />
                      {isAutoSaving ? 'جاري الحفظ...' : 'الشهادة'}
                    </button>
                    
                    <button
                      onClick={() => saveResults(false)}
                      disabled={isSaving || isRedirecting}
                      className={`py-2 px-3 font-medium rounded-lg transition-all duration-300 flex items-center justify-center text-xs ${
                        isSaving || isRedirecting
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      <FaSave className="ml-1 text-xs" />
                      {isSaving ? 'جاري الحفظ...' : 'حفظ النتائج'}
                    </button>
                  </div>
                </div>
              )}
              
               {activeTab === 'levels' && (
                <div className="space-y-3">
                  {results.levelResults.map((levelResult, index) => (
                    <div 
                      key={index}
                      className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden"
                    >
                      <div 
                        className="p-2 flex justify-between items-center cursor-pointer hover:bg-gray-100"
                        onClick={() => toggleLevel(index)}
                      >
                        <div className="flex items-center">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ml-1 text-xs ${
                            levelResult.score >= 90 ? 'bg-green-100 text-green-600' :
                            levelResult.score >= 70 ? 'bg-blue-100 text-blue-600' :
                            levelResult.score >= 50 ? 'bg-amber-100 text-amber-600' :
                            'bg-red-100 text-red-600'
                          }`}>
                            {levelResult.score >= 90 ? <FaTrophy /> : 
                             levelResult.score >= 70 ? <FaMedal /> : 
                             levelResult.score >= 50 ? <FaRegSmile /> : 
                             <FaBook />}
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 text-sm">{levelResult.name}</h4>
                            <div className="text-xs text-gray-500">
                              {levelResult.correctAnswers}/{levelResult.totalQuestions}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className={`text-sm font-bold ml-2 ${
                            levelResult.score >= 90 ? 'text-green-600' :
                            levelResult.score >= 70 ? 'text-blue-600' :
                            levelResult.score >= 50 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {levelResult.score}%
                          </div>
                          {expandedLevels[index] ? (
                            <FaChevronUp className="text-gray-400 text-xs" />
                          ) : (
                            <FaChevronDown className="text-gray-400 text-xs" />
                          )}
                        </div>
                      </div>
                      
                      {expandedLevels[index] && (
                        <div className="p-2 border-t border-gray-200 bg-white text-xs">
                          <div className="mb-2">
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
                              <div 
                                className={`h-1.5 rounded-full ${
                                  levelResult.score >= 90 ? 'bg-green-500' :
                                  levelResult.score >= 70 ? 'bg-blue-500' :
                                  levelResult.score >= 50 ? 'bg-amber-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${levelResult.score}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          <div className="space-y-1">
                            <div className="flex justify-between">
                              <span className="text-gray-500">الأسئلة</span>
                              <span>{levelResult.totalQuestions}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">الصحيحة</span>
                              <span className="text-green-600">{levelResult.correctAnswers}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">الخاطئة</span>
                              <span className="text-red-600">{levelResult.wrongAnswers}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">الدقة</span>
                              <span className="font-bold">{levelResult.score}%</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
               {activeTab === 'details' && (
                <div className="space-y-3">
                  {currentTest.levels?.map((level, levelIndex) => {
                    const startIndex = currentTest.levels.slice(0, levelIndex).reduce((total, l) => 
                      total + (l.questions?.length || 0), 0);
                    
                    return (
                      <div key={levelIndex} className="mb-3">
                        <div 
                          className="flex justify-between items-center cursor-pointer bg-gray-50 p-2 rounded-lg hover:bg-gray-100 transition-all mb-2"
                          onClick={() => toggleLevel(levelIndex + 100)}
                        >
                          <h3 className="text-sm font-bold text-gray-900 flex items-center">
                            <span className="w-5 h-5 bg-[#320A6B] rounded text-white text-xs flex items-center justify-center ml-1">{levelIndex + 1}</span>
                            {level.levelTitle || level.name || `المستوى ${levelIndex + 1}`}
                          </h3>
                          {expandedLevels[levelIndex + 100] ? (
                            <FaChevronUp className="text-gray-400 text-xs" />
                          ) : (
                            <FaChevronDown className="text-gray-400 text-xs" />
                          )}
                        </div>
                        
                        {expandedLevels[levelIndex + 100] && level.questions && (
                          <div className="space-y-2 animate-fade-in">
                            {level.questions.map((question, questionIndex) => {
                              const answerIndex = startIndex + questionIndex;
                              const userAnswer = userAnswers[answerIndex];
                              const isCorrect = userAnswer?.isCorrect === true;
                              const isTimeUp = userAnswer?.timeUp === true;
                              
                              return (
                                <div 
                                  key={questionIndex} 
                                  className={`p-2 rounded-lg border text-xs ${
                                    isCorrect ? 'bg-green-50 border-green-200' : 
                                    'bg-red-50 border-red-200'
                                  }`}
                                >
                                  <div className="flex justify-between items-center mb-1">
                                    <h4 className="font-bold text-gray-900 flex items-center">
                                      <span className={`w-4 h-4 rounded text-white text-xs flex items-center justify-center ml-1 ${
                                        isCorrect ? 'bg-green-500' : 'bg-red-500'
                                      }`}>
                                        {questionIndex + 1}
                                      </span>
                                      سؤال {questionIndex + 1}
                                      {isTimeUp && (
                                        <span className="mr-1 text-red-500 text-xs">⏰</span>
                                      )}
                                    </h4>
                                    <div className={`flex items-center ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
                                      {isCorrect ? <FaCheck className="ml-1" /> : <FaTimes className="ml-1" />}
                                      <span className="text-xs mr-1">{userAnswer?.timeSpent || 0}ث</span>
                                    </div>
                                  </div>
                                  
                                  <p className="text-gray-800 mb-2">{question.questionText}</p>
                                  
                                  <div className="space-y-1">
                                    {question.options.map((option, optionIndex) => {
                                      const isCorrectOption = option === question.correctAnswer;
                                      const isUserSelected = userAnswer?.selectedIndex === optionIndex;
                                      
                                      let optionClass = "p-1 rounded text-xs ";
                                      
                                      if (isCorrectOption) {
                                        optionClass += "bg-green-100 text-green-800 border border-green-200";
                                      } else if (isUserSelected) {
                                        optionClass += "bg-red-100 text-red-800 border border-red-200";
                                      } else {
                                        optionClass += "bg-gray-100 text-gray-700 border border-gray-200";
                                      }
                                      
                                      return (
                                        <div 
                                          key={optionIndex}
                                          className={optionClass}
                                        >
                                          <div className="flex items-center">
                                            <div className={`w-3 h-3 rounded flex items-center justify-center ml-1 text-xs ${
                                              isCorrectOption
                                                ? 'bg-green-500 text-white'
                                                : isUserSelected
                                                  ? 'bg-red-500 text-white'
                                                  : 'bg-gray-300 text-gray-500'
                                            }`}>
                                              {String.fromCharCode(1632 + optionIndex + 1)}
                                            </div>
                                            {option}
                                            {isCorrectOption && <span className="mr-auto text-green-600 text-xs">✓</span>}
                                            {isUserSelected && !isCorrectOption && <span className="mr-auto text-red-600 text-xs">✗</span>}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer scrollToSection={scrollToSection} />
      
      <RatingForm />

      <style jsx>{`
        .floating {
          animation: float 8s ease-in-out infinite;
          pointer-events: none;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(var(--rotation, 0deg));
          }
          50% {
            transform: translateY(-8px) rotate(var(--rotation, 0deg));
          }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @media (max-width: 767px) {
          .floating {
            animation: float 10s ease-in-out infinite;
          }
        }
      `}</style>
    </div>
  );
};

export default TestResultsScreen;